import * as Ably from 'ably'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { useWebRegisterStatusManager } from '../../ng-compat/hooks'
import { useFetchPairing } from '../payments/lspay-payment-modal/hooks/useFetchPairing'
import { LSPAYPairing } from '../payments/lspay-payment-modal/helpers/lspay.types'
import { getFeatureFromCache } from '../../util/feature-cache'

const FEATURE_TERMINAL_STATUS_CONNECTION_LOGGING_ENABLED =
  'lspay_terminal_status_connection_logging_enabled'

export type RealtimeContextState = {
  client: Ably.Realtime | null
  pairing: LSPAYPairing | null
}

export const RealtimeContext = createContext<RealtimeContextState>({
  client: null,
  pairing: null,
})

interface RealtimeProviderProps {
  outletId: string
  registerId: string
  children: ReactNode
}

const getAuthUrl = (registerId: string) =>
  `/api/1.0/cfd/lspay/auth/${registerId}`

export function RealtimeProvider({
  outletId,
  registerId,
  children,
}: RealtimeProviderProps) {
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const isOnline = webRegisterStatusManager?.isOnline() ?? false

  // Fetch pairing data using the existing hook
  const { loading, pairing, error } = useFetchPairing(registerId)

  const [client, setClient] = useState<Ably.Realtime | null>(null)
  const [pairingData, setPairingData] = useState<LSPAYPairing | null>(null)

  // Keep a ref to the latest pairing so the effect can read it without
  // adding the unstable object reference to the dependency array.
  const pairingRef = useRef(pairing)
  pairingRef.current = pairing

  // Derive a stable primitive key from the pairing so the effect re-runs
  // only when the actual pairing data changes, not on every Apollo re-render.
  const pairingKey = pairing
    ? `${pairing.registerID}:${pairing.deviceSN}:${pairing.deviceID}:${pairing.deviceType}:${pairing.appVersion}`
    : undefined
  const pairingDeviceSN = pairing?.deviceSN
  const errorMessage = error?.message

  useEffect(() => {
    const terminalStatusConnectionLoggingEnabled = getFeatureFromCache(
      FEATURE_TERMINAL_STATUS_CONNECTION_LOGGING_ENABLED
    )

    // Check if prerequisites are met
    const shouldSetupClient =
      !loading &&
      !errorMessage &&
      pairingKey &&
      isOnline &&
      registerId &&
      outletId

    if (!shouldSetupClient) {
      setClient(null)
      setPairingData(null)
      return
    }

    let ablyClient: Ably.Realtime | null = null
    let cancelled = false

    const setupAblyClient = () => {
      if (cancelled) {
        return
      }

      setPairingData(pairingRef.current ?? null)

      const newClient = new Ably.Realtime({
        authUrl: getAuthUrl(registerId),
        closeOnUnload: true,
      })

      if (cancelled) {
        newClient.close()
        return
      }

      ablyClient = newClient
      setClient(ablyClient)

      if (terminalStatusConnectionLoggingEnabled) {
        datadogLogs.logger.info('Provisioned Realtime Client', {
          provider: 'ably',
          client: ablyClient.clientId,
          outletId,
          registerId,
          terminalSerialNumber: pairingDeviceSN,
          connectionId: ablyClient.connection.id,
        })
      }
    }

    setupAblyClient()

    return () => {
      cancelled = true

      if (ablyClient && terminalStatusConnectionLoggingEnabled) {
        datadogLogs.logger.info('Closed Realtime Client', {
          provider: 'ably',
          client: ablyClient.clientId,
          outletId,
          registerId,
          connectionId: ablyClient.connection.id,
        })
      }

      ablyClient?.close()
    }
  }, [
    isOnline,
    outletId,
    registerId,
    loading,
    pairingKey,
    pairingDeviceSN,
    errorMessage,
  ])

  return (
    <RealtimeContext.Provider value={{ client, pairing: pairingData }}>
      {children}
    </RealtimeContext.Provider>
  )
}

export function useRealtimeProvider(): RealtimeContextState {
  return useContext(RealtimeContext)
}
